// lib/feed/live-strip-persistence.ts
//
// Pure domain for persisting the LiveFeed (default "live" tab) chip-strip
// filters across navigation — sessionStorage, per-user key, TTL, and the
// initial-state precedence decision (persisted user edits > resolved-prefs
// cache > empty). Reuses `coerceFeedFilters` from `feed-filters-persistence.ts`
// for per-field defensive coercion rather than duplicating it — `FeedFiltersState`
// (components/feed/feed-filters.tsx) is structurally the same shape as
// `FeedFilters` (feed-page-client-state.ts): brands/themes/categories/tcgs/
// conditions/gradeRange/listingFormats/tcgSets/tcgRarities/cardConditions/
// cardLanguages/cardFormats/gradingCompanies/cardGradeRange/listingCategory/
// mtgPrintVariants/sortBy — same fields, same optionality, just two separately
// declared types (LiveFeed's own local strip type predates the shared
// `FeedFilters` alias). Left as-is here; unifying the two declarations is a
// separate follow-up, out of scope for this fix.
//
// WHY: LiveFeed is the only feed tab that does NOT receive a `filters` prop
// from `feed-page-client-{mobile,desktop}.tsx` (`components/feed/live-feed.tsx`
// takes only `currentUserId` + `refreshSignal`). It owns a SEPARATE `strip`
// state, seeded from the resolved-collector-preferences cache
// (`lib/feed/prefs-strip-cache.ts` — a cache of SERVER preference resolution,
// NOT of user-applied chip edits) and never persisted. So
// `lib/feed/feed-filters-persistence.ts` (which persists
// `useFeedPageClientState.filters`, consumed by the for-you/discover tabs)
// has NO effect on "live": back-nav re-seeds `strip` from prefs (or
// EMPTY_STRIP), the user's applied chips are gone, `criteria` (derived from
// `strip`) differs from what it was, and `usePaginatedFeed`'s
// `{cacheKey: "live"}` snapshot key changes → cache miss → items/page/scroll
// look reset too, even though the snapshot itself is still in storage.
//
// @see components/feed/live-feed.tsx (adapter)
// @see lib/feed/feed-filters-persistence.ts (coercion this module reuses; twin
//   persistence for the OTHER filters owner — for-you/discover)
// @see lib/feed/prefs-strip-cache.ts (sibling cache — resolved SERVER prefs,
//   lower precedence than this module's persisted USER edits)
// @see .tondocs/skills/hex.md §1 (functional core, imperative shell)

import type { FeedFiltersState } from "@/components/feed/feed-filters";
import { coerceFeedFilters } from "@/lib/feed/feed-filters-persistence";
import { FEED_ACTIVE_TAB_TTL_MS } from "@/lib/feed/feed-active-tab-persistence";

/** Namespace all live-strip persistence keys; the per-user id is appended. */
export const LIVE_STRIP_STORAGE_PREFIX = "kraahl_feed_live_strip:";

/** Bump when the persisted shape changes — old entries are then rejected on read. */
export const LIVE_STRIP_VERSION = 1;

/**
 * Same TTL as the active tab / for-you-discover filters (`FEED_ACTIVE_TAB_TTL_MS`)
 * — all three feed-page persistence entries (tab, shared filters, live strip)
 * restore or expire together, so a remount never mixes a fresh tab with stale
 * filters or vice versa.
 */
export const LIVE_STRIP_TTL_MS = FEED_ACTIVE_TAB_TTL_MS;

/**
 * Per-(user|anon) key — logged-in users and logged-out sessions each get
 * their own entry, same as `lib/feed/prefs-strip-cache.ts`'s per-user keying,
 * except this cache also covers logged-out browsing (prefs-strip-cache never
 * runs for anon — there's nothing to resolve — but anon users can still
 * apply live-feed chip filters and expect back-nav to restore them).
 */
export function buildLiveStripStorageKey(userId: string | undefined): string {
  return `${LIVE_STRIP_STORAGE_PREFIX}${userId ?? "anon"}`;
}

interface StoredLiveStrip {
  v: number;
  strip: FeedFiltersState;
  ts: number;
}

/** Serialize the strip + write timestamp for storage. */
export function serializeLiveStrip(strip: FeedFiltersState, now: number): string {
  const payload: StoredLiveStrip = { v: LIVE_STRIP_VERSION, strip, ts: now };
  return JSON.stringify(payload);
}

/**
 * Decide which strip (if any) to restore from a raw sessionStorage value:
 * parse, validate shape/version, check freshness, then defensively coerce
 * every field (delegated to `coerceFeedFilters` — `FeedFiltersState` and
 * `FeedFilters` are structurally identical, see header). Returns `null` when
 * nothing should be restored (missing/malformed/expired/wrong-version) — the
 * caller falls back to the resolved-prefs cache, then `EMPTY_STRIP`. Pure —
 * `now`/`ttlMs` are injected so tests are deterministic.
 */
export function decideRestoredLiveStrip(raw: string | null, now: number, ttlMs: number): FeedFiltersState | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const p = parsed as Partial<StoredLiveStrip>;
  if (p.v !== LIVE_STRIP_VERSION) return null;
  const ts = typeof p.ts === "number" ? p.ts : 0;
  if (now - ts > ttlMs) return null;
  if (typeof p.strip !== "object" || p.strip === null) return null;
  return coerceFeedFilters(p.strip) as FeedFiltersState;
}

export type LiveStripPhase = "pending" | "resolved";

export interface InitialLiveStripState {
  strip: FeedFiltersState;
  phase: LiveStripPhase;
  /**
   * True when `strip` came from THIS module (persisted user edits), as
   * opposed to the prefs cache or the empty default. The adapter uses this to
   * implement the "restore-wins" rule: `loadPrefsIntoStrip`'s background
   * revalidate must not clobber a restored strip with prefs-derived values,
   * even though it still refreshes the prefs cache itself for future mounts.
   */
  restoredFromPersistence: boolean;
}

/**
 * Precedence for LiveFeed's initial `strip` + `prefsPhase`:
 *   1. persisted user-edit strip (this module) — wins if present.
 *   2. resolved-prefs cache partial (`lib/feed/prefs-strip-cache.ts`).
 *   3. `emptyStrip`, with phase "pending" (prefs resolution still needed).
 *
 * Both (1) and (2) resolve `phase` as "resolved" immediately — the strip
 * already holds a real (or intentionally empty) value, so the feed fetch
 * doesn't need to wait. Pure: `emptyStrip` is injected rather than imported
 * from the component so this stays testable without a live-feed.tsx import.
 */
export function decideInitialLiveStripState(
  persistedStrip: FeedFiltersState | null,
  prefsCachePartial: Partial<FeedFiltersState> | null,
  emptyStrip: FeedFiltersState
): InitialLiveStripState {
  if (persistedStrip) {
    return { strip: persistedStrip, phase: "resolved", restoredFromPersistence: true };
  }
  if (prefsCachePartial) {
    return { strip: { ...emptyStrip, ...prefsCachePartial }, phase: "resolved", restoredFromPersistence: false };
  }
  return { strip: emptyStrip, phase: "pending", restoredFromPersistence: false };
}
